import { ConfirmOptions, useConfirmation } from '@wyz/ui';
import { useI18n } from '~/common/context';
import { ICustomer } from '~/common/types';
import { CustomerUtils } from '~/connect/helpers';
import { CustomerFormStep } from '~/connect/types';
import { useCustomerForm } from '~/connect/hooks';

type SubmitGuard = (
  forms: ReturnType<typeof useCustomerForm>['forms'],
  customer: ICustomer,
) => { confirmOptions: ConfirmOptions; onCancel: () => void } | null;

export function useCustomerSubmitGuard() {
  const { t } = useI18n();
  const ask = useConfirmation();

  const SUBMIT_GUARDS: Partial<Record<CustomerFormStep, SubmitGuard>> = {
    GENERAL_FORM: (forms, customer) => {
      const currentCode =
        forms[CustomerUtils.GENERAL_FORM_STEP].methods.getValues(
          'customerCode',
        );
      const originalCode = customer.code;
      if (currentCode === originalCode) return null;
      return {
        confirmOptions: {
          title: t('app.entity.form.code_change.modal.title'),
          description: t('app.entity.form.code_change.modal.description'),
          confirmBtnText: t('app.common.buttons.update'),
          cancelBtnText: t('app.common.button.cancel'),
        },
        onCancel: () => {
          forms[CustomerUtils.GENERAL_FORM_STEP].methods.setValue(
            'customerCode',
            originalCode ?? '',
          );
        },
      };
    },
  };

  return (
    editableSection: CustomerFormStep | undefined,
    forms: ReturnType<typeof useCustomerForm>['forms'],
    customer: ICustomer,
    executeSubmit: () => void,
  ) => {
    if (!editableSection) {
      executeSubmit();
      return;
    }
    const guard = SUBMIT_GUARDS[editableSection];
    if (!guard) {
      executeSubmit();
      return;
    }
    const result = guard(forms, customer);
    if (!result) {
      executeSubmit();
      return;
    }
    ask(result.confirmOptions)
      .onConfirm(() => executeSubmit())
      .onCancel(result.onCancel);
  };
}
